import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { noteMealSaved } from "@/lib/reviewPrompt";
import { ItemRow } from "@/components/MealItemEditor";
import { X, Loader2, Check, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { invalidateSWR } from "@/lib/swr";
import { todayISO } from "@/lib/utils";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n.jsx";

/**
 * AddRecipeToMealModal
 *
 * Bottom-sheet modal that lets the trainee log an existing recipe as a
 * meal for a chosen date + meal_type + servings. Posts to
 * `POST /api/meals/from-recipe` which scales the recipe's per-serving
 * macros and persists a Meal document.
 *
 * Bilingual (AR/EN) + future-date guard + horizontal-scroll lock —
 * same pattern as WeightLogModal so the two modals feel identical.
 *
 * Props:
 *   recipe    — the recipe object being logged (must include id + macros)
 *   onClose   — called on cancel / after successful save
 *   onSaved   — optional callback receiving the created Meal
 */
export const mealTypeByTime = (h = new Date().getHours()) =>
  h < 4 ? "snack" : h < 11 ? "breakfast" : h < 16 ? "lunch" : h < 18 ? "snack" : h < 23 ? "dinner" : "snack";

export const MEAL_TYPES = [
  { key: "breakfast", ar: "فطور",  en: "Breakfast" },
  { key: "lunch",     ar: "غداء",  en: "Lunch" },
  { key: "dinner",    ar: "عشاء",  en: "Dinner" },
  { key: "snack",     ar: "سناك",  en: "Snack" },
];

export default function AddRecipeToMealModal({ recipe, kind = "user", onClose, onSaved, initialDate, initialMealType, initialServings, stay = false, onDelete }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { lang } = useI18n();
  const en = lang === "en";
  // Freeze TODAY at mount so the clamp stays consistent even if the
  // modal is left open past midnight (same safeguard as WeightLogModal).
  const TODAY = todayISO();
  const clampDate = (v) => (v && v > TODAY ? TODAY : v || TODAY);

  const [date, setDate] = useState(() => clampDate(initialDate || params.get("date")));
  const [mealType, setMealType] = useState(() => {
    const t = initialMealType || params.get("type");
    return MEAL_TYPES.some((m) => m.key === t) ? t : mealTypeByTime();
  });
  const [servings, setServings] = useState(initialServings || 1.0);
  const [saving, setSaving] = useState(false);
  // Per-serving ingredient breakdown; `qty` = user override in total grams.
  const [rows, setRows] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(true);
  // Extra ingredients added just for this log — fixed amounts, not scaled by servings.
  const [extras, setExtras] = useState([]);
  const addExtra = () => setExtras((x) => [...x, { _k: `x-${Date.now()}`, name: "", quantity_g: 0, unit: "g", calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }]);
  const updExtra = (k, patch) => setExtras((x) => x.map((e) => (e._k === k ? { ...e, ...patch } : e)));
  useEffect(() => {
    if (!recipe?.id) return;
    api.get(`/meals/recipe-items/${recipe.id}`, { params: { lang } })
      .then(({ data }) => setRows((data.items || []).map((it) => ({ ...it, qty: null, removed: false }))))
      .catch(() => setRows([]))
      .finally(() => setRowsLoading(false));
  }, [recipe?.id, lang]);

  const sv = Math.max(0.1, Math.min(20, Number(servings) || 1));
  const liveItems = useMemo(() => (rows || []).filter((r) => !r.removed).map((r) => {
    const g = r.qty ?? Math.round(r.quantity_g * sv * 10) / 10;
    const k = g / 100;
    const p = r.per_100g || {};
    return {
      name: r.name, unit: r.unit || "g", quantity_g: g, per_100g: p,
      calories: Math.round((p.calories || 0) * k),
      protein_g: Math.round((p.protein_g || 0) * k * 10) / 10,
      carbs_g: Math.round((p.carbs_g || 0) * k * 10) / 10,
      fat_g: Math.round((p.fat_g || 0) * k * 10) / 10,
    };
  }), [rows, sv]);
  const hasRows = (rows || []).length > 0;
  const extraItems = extras.filter((e) => (e.name || "").trim()).map(({ _k, ...e }) => ({
    ...e, name: e.name.trim(), quantity_g: Number(e.quantity_g) || 0,
    calories: Number(e.calories) || 0, protein_g: Number(e.protein_g) || 0, carbs_g: Number(e.carbs_g) || 0, fat_g: Number(e.fat_g) || 0,
  }));

  // Without a breakdown the recipe is one roll-up item scaled by servings.
  const recipeItems = useMemo(() => (hasRows ? liveItems : [{
    name: recipe?.title || (en ? "Recipe" : "وصفة"), quantity_g: Math.round(100 * sv),
    calories: Math.round((recipe?.calories_per_serving || 0) * sv),
    protein_g: Math.round((recipe?.protein_g || 0) * sv * 10) / 10,
    carbs_g: Math.round((recipe?.carbs_g || 0) * sv * 10) / 10,
    fat_g: Math.round((recipe?.fat_g || 0) * sv * 10) / 10,
    assumption: en ? `${sv} serving(s) of the recipe` : `${sv} حصة من الوصفة`,
  }]), [hasRows, liveItems, recipe, sv, en]);
  const allItems = [...recipeItems, ...extraItems];
  const perServingCal = Math.round(allItems.reduce((a, i) => a + (Number(i.calories) || 0), 0) / sv);
  const changed = extraItems.length > 0 || (rows || []).some((r) => r.removed || r.qty != null);
  const [keep, setKeep] = useState(false);
  const scaleItems = (list, k) => list.map((i) => ({
    name: i.name, unit: i.unit || "g", quantity_g: Math.round((Number(i.quantity_g) || 0) * k * 10) / 10,
    calories: (Number(i.calories) || 0) * k, protein_g: (Number(i.protein_g) || 0) * k,
    carbs_g: (Number(i.carbs_g) || 0) * k, fat_g: (Number(i.fat_g) || 0) * k,
  }));
  // Persist the edited amounts: own recipe → update it; platform recipe → personal copy.
  const keepChanges = async () => {
    if (kind === "platform") {
      await api.post(`/user-recipes/copy-platform/${recipe.id}`, { items: scaleItems(allItems, 1 / sv) });
    } else {
      await api.patch(`/user-recipes/${recipe.id}`, { items: scaleItems(allItems, Math.max(1, Number(recipe.servings) || 1) / sv) });
    }
  };

  const scaled = useMemo(() => {
    const sum = (m) => allItems.reduce((a, i) => a + (Number(i[m]) || 0), 0);
    return { cal: Math.round(sum("calories")), p: Math.round(sum("protein_g") * 10) / 10, c: Math.round(sum("carbs_g") * 10) / 10, f: Math.round(sum("fat_g") * 10) / 10 };
  }, [allItems]);

  const save = async () => {
    // Belt-and-suspenders — even if the input `max` was bypassed
    // (iOS keyboard, paste, DevTools), refuse future-dated meals.
    // Matches the backend `not_future_date` validator so the UI errors
    // stay consistent.
    if (date > TODAY) {
      toast.error(en ? "Date can't be in the future" : "لا يمكن اختيار تاريخ في المستقبل");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/meals/from-recipe", {
        recipe_id: recipe.id,
        date,
        meal_type: mealType,
        servings: Number(servings) || 1,
        ...(hasRows || extraItems.length ? { items: allItems } : {}),
      });
      // Backend auto-populated DayNotes ingredients from the recipe items;
      // clear the cached notes doc so the dashboard picks them up.
      invalidateSWR(`notes:${date}`);
      if (keep && changed) {
        try { await keepChanges(); } catch { toast.error(en ? "Meal added, but saving your version failed" : "أُضيفت الوجبة، لكن تعذّر حفظ نسختك"); }
      }
      toast.success(keep && changed
        ? (en ? "Added — and your version is saved" : "تمت الإضافة وحُفظت نسختك")
        : (en ? "Recipe added to your meals" : "تمّت إضافة الوصفة إلى وجباتك"));
      onSaved?.(data);
      onClose?.();
      noteMealSaved();
      if (!stay) nav(date === todayISO() ? "/app" : `/app?date=${date}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (en ? "Add failed" : "تعذّرت الإضافة"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      dir={en ? "ltr" : "rtl"}
      data-testid="add-recipe-to-meal-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md rounded-3xl overflow-hidden max-h-[92vh] flex flex-col"
        style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
      >
        <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center"
            data-testid="add-recipe-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="font-bold">{en ? "Add recipe to my meals" : "إضافة الوصفة إلى وجباتي"}</div>
        </div>

        <div
          className="p-5 space-y-4 overflow-y-auto overflow-x-hidden"
          style={{ overscrollBehavior: "contain" }}
        >
          <div className="bg-brand-50/50 rounded-2xl p-3 text-start">
            <div className="text-[11px] text-neutral-500">{en ? "Recipe" : "الوصفة"}</div>
            <div className="font-bold text-neutral-900 truncate">{recipe?.title || "—"}</div>
          </div>

          {/* Date */}
          <div>
            <div className="text-xs text-neutral-500 mb-1.5 text-start font-semibold">{en ? "Date" : "التاريخ"}</div>
            <input
              type="date"
              value={date}
              max={TODAY}
              onChange={(e) => setDate(clampDate(e.target.value))}
              className="w-full bg-white shadow-card rounded-2xl px-4 py-3 text-start focus:outline-none"
              data-testid="add-recipe-date"
            />
          </div>

          {/* Meal type */}
          <div>
            <div className="text-xs text-neutral-500 mb-1.5 text-start font-semibold">{en ? "Meal type" : "نوع الوجبة"}</div>
            <div className="grid grid-cols-4 gap-2">
              {MEAL_TYPES.map((m) => {
                const active = mealType === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMealType(m.key)}
                    className={`py-2.5 rounded-2xl text-sm font-bold transition ${
                      active
                        ? "orange-gradient text-white shadow"
                        : "bg-neutral-100 text-neutral-600"
                    }`}
                    data-testid={`add-recipe-meal-${m.key}`}
                  >
                    {en ? m.en : m.ar}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Servings */}
          <div>
            <div className="text-xs text-neutral-500 mb-1.5 text-start font-semibold">
              {en
                ? `Servings (each = ${perServingCal} kcal)`
                : `الحصص (كل حصة = ${perServingCal} سعرة)`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setServings((s) => Math.max(0.1, Math.round(((Number(s) || 1) - 0.5) * 10) / 10))}
                className="w-11 h-11 rounded-full bg-white shadow-card flex items-center justify-center font-black text-lg text-brand-500 active:scale-95"
                data-testid="add-recipe-servings-minus"
              >−</button>
              <input
                type="number"
                inputMode="decimal"
                min="0.1"
                max="20"
                step="0.5"
                value={servings}
                placeholder="1"
                onChange={(e) => setServings(e.target.value === "" ? "" : Number(e.target.value))}
                onBlur={(e) => { if (e.target.value === "" || Number(e.target.value) < 0.1) setServings(1); }}
                className="flex-1 bg-white shadow-card rounded-2xl px-4 py-3 text-center text-xl font-black num focus:outline-none placeholder:text-neutral-300"
                dir="ltr"
                data-testid="add-recipe-servings"
              />
              <button
                type="button"
                onClick={() => setServings((s) => Math.min(20, Math.round(((Number(s) || 1) + 0.5) * 10) / 10))}
                className="w-11 h-11 rounded-full bg-white shadow-card flex items-center justify-center font-black text-lg text-brand-500 active:scale-95"
                data-testid="add-recipe-servings-plus"
              >+</button>
            </div>
          </div>

          <IngredientRows rows={rows} loading={rowsLoading} sv={sv} en={en}
            onQty={(i, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, qty: v } : r)))}
            onRemove={(i) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, removed: true } : r)))} />

          {!rowsLoading && (
            <div className="space-y-2" data-testid="add-recipe-extras">
              {extras.map((it, idx) => (
                <ItemRow key={it._k} item={it} idx={`x${idx}`} onChange={(p) => updExtra(it._k, p)}
                  onRemove={() => setExtras((x) => x.filter((e) => e._k !== it._k))} />
              ))}
              <button type="button" onClick={addExtra}
                className="w-full py-2.5 rounded-2xl border-2 border-dashed border-brand-200 text-brand-600 text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors"
                data-testid="add-recipe-add-extra">
                <Plus className="w-4 h-4" /> {en ? "Add ingredient" : "إضافة مكوّن"}
              </button>
            </div>
          )}

          {/* Preview */}
          <div className="grid grid-cols-4 gap-2 bg-neutral-50 rounded-2xl p-3" data-testid="add-recipe-preview">
            <div className="text-center">
              <div className="text-[10px] text-neutral-500">{en ? "Calories" : "سعرات"}</div>
              <div className="font-bold num text-neutral-900">{scaled.cal}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-neutral-500">{en ? "Protein" : "بروتين"}</div>
              <div className="font-bold num text-sky-600">{scaled.p}g</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-neutral-500">{en ? "Carbs" : "كارب"}</div>
              <div className="font-bold num text-emerald-600">{scaled.c}g</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-neutral-500">{en ? "Fat" : "دهون"}</div>
              <div className="font-bold num text-orange-500">{scaled.f}g</div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-neutral-100">
          {changed && (
            <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700 bg-brand-50 rounded-2xl px-3 py-2.5 mb-2 cursor-pointer" data-testid="add-recipe-keep-label">
              <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} className="w-4 h-4 accent-orange-500" data-testid="add-recipe-keep" />
              {kind === "platform"
                ? (en ? "Save these changes as a copy in my saved recipes" : "احفظ هذه التعديلات كنسخة في وصفاتي المحفوظة")
                : (en ? "Also update the saved recipe with these changes" : "حدّث الوصفة المحفوظة بهذه التعديلات")}
            </label>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="w-full orange-gradient text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="add-recipe-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {en ? "Add to my meals" : "أضف إلى وجباتي"}
          </button>
          {onDelete && (
            <button onClick={onDelete} className="w-full mt-2 py-2 text-sm font-semibold text-red-600 hover:text-red-700 flex items-center justify-center gap-1.5" data-testid="add-recipe-delete">
              <Trash2 className="w-4 h-4" /> {en ? "Remove from saved" : "حذف من المحفوظات"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function IngredientRows({ rows, loading, sv, en, onQty, onRemove }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-neutral-500 bg-neutral-50 rounded-2xl p-3" data-testid="add-recipe-items-loading">
        <Loader2 className="w-4 h-4 animate-spin text-brand-500" /> {en ? "Preparing ingredients…" : "جارٍ تجهيز المكونات…"}
      </div>
    );
  }
  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid="add-recipe-items">
      <div className="text-xs text-neutral-500 mb-1.5 text-start font-semibold">{en ? "Ingredients (edit amounts)" : "المكونات (عدّل الكميات)"}</div>
      <div className="space-y-2">
        {rows.map((r, i) => {
          if (r.removed) return null;
          const g = r.qty ?? Math.round(r.quantity_g * sv * 10) / 10;
          const cal = Math.round(((r.per_100g?.calories) || 0) * g / 100);
          return (
            <div key={i} className="flex items-center gap-2 bg-white shadow-card rounded-2xl px-3 py-2" data-testid={`add-recipe-item-${i}`}>
              <div className="flex-1 min-w-0 text-start">
                <div className="text-sm font-bold text-neutral-800 truncate">{r.name}</div>
                <div className="text-[11px] text-neutral-500 num" data-testid={`add-recipe-item-cal-${i}`}>{cal} {en ? "kcal" : "سعرة"}</div>
              </div>
              <input
                type="number" inputMode="decimal" min="0" value={g} dir="ltr"
                onChange={(e) => onQty(i, e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))}
                className="w-20 bg-neutral-50 rounded-xl px-2 py-1.5 text-center font-bold num focus:outline-none"
                data-testid={`add-recipe-item-qty-${i}`}
              />
              <span className="text-[11px] text-neutral-500 w-5">{r.unit === "ml" ? (en ? "ml" : "مل") : (en ? "g" : "غ")}</span>
              <button type="button" onClick={() => onRemove(i)} className="p-1.5 rounded-full hover:bg-red-50 text-neutral-400 hover:text-red-500" data-testid={`add-recipe-item-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
